import { useEffect } from "preact/hooks";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service — webhookstorage";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Terms of service for webhookstorage — usage rights, SLA, data retention, and acceptable use.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://webhookstorage.com/terms");

    window.scrollTo(0, 0);
  }, []);

  return (
    <div class="min-h-screen bg-white text-slate-900">
      <PublicHeader />
      <div class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="mb-2 text-3xl font-bold tracking-tight">Terms of Service</h1>
        <p class="mb-1 text-sm text-slate-500">
          Effective date: March 11, 2026
        </p>
        <p class="mb-10 text-sm text-slate-500">
          Last updated: March 11, 2026
        </p>

        <div class="space-y-8 text-sm leading-relaxed text-slate-700">

          {/* ── Section 1 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">1. Agreement to Terms</h2>
            <p class="mb-3">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("Customer," "you," or "your") and <strong>Sammons Software LLC</strong>, a Missouri limited liability company ("Company," "we," "us," or "our"), governing your access to and use of the webhookstorage.com platform and related services (collectively, the "Service").
            </p>
            <p class="mb-3">
              By creating an account, accessing, or using the Service, you agree to be bound by these Terms. If you are using the Service on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms. If you do not agree, do not use the Service.
            </p>
            <p>
              We may update these Terms from time to time. We will notify you of material changes by email or through the Service at least thirty (30) days before they take effect. Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the Service before the effective date.
            </p>
          </section>

          {/* ── Section 2 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">2. Description of Service</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">2.1 What We Do</h3>
            <p class="mb-3">webhookstorage.com is a webhook payload buffering and retrieval service. The Service:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li><strong>Receives</strong> inbound HTTP webhook payloads on your behalf at unique, authenticated endpoints.</li>
              <li><strong>Stores</strong> those payloads securely in encrypted cloud storage, scoped to your account and endpoints.</li>
              <li><strong>Makes payloads available</strong> for retrieval via authenticated REST API, enabling downstream consumption by automation platforms, custom applications, or other integrations.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">2.2 What We Do Not Do</h3>
            <p class="mb-3">The Service does not:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Inspect, parse, validate, modify, or transform the content of your webhook payloads.</li>
              <li>Deliver payloads outbound to third-party destinations (the Service operates on a polling/retrieval model).</li>
              <li>Guarantee the accuracy, legality, or fitness of any data you store through the Service.</li>
              <li>Provide backup or disaster-recovery services beyond the retention and redundancy described in Section 8.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">2.3 Service Participants</h3>
            <p class="mb-3">Three categories of participants interact with the Service:</p>
            <ul class="ml-5 list-disc space-y-2">
              <li><strong>Customer (you):</strong> The account holder who configures endpoints, manages API keys, and is responsible for billing.</li>
              <li><strong>Webhook Senders:</strong> Third-party systems or services that transmit webhook payloads to your endpoints. You are responsible for authorizing and configuring these senders.</li>
              <li><strong>Consumers:</strong> Systems, automation platforms, or applications that retrieve stored payloads using your API keys. You are responsible for securing and managing consumer access.</li>
            </ul>
          </section>

          {/* ── Section 3 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">3. Account Registration and Security</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.1 Account Creation</h3>
            <p class="mb-4">
              To use the Service, you must create an account. Authentication is provided through our identity partner (currently WorkOS AuthKit). You may authenticate via magic link or passkey (WebAuthn). We do not store passwords.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.2 Account Eligibility</h3>
            <p class="mb-4">
              You must be at least eighteen (18) years old and capable of forming a binding contract under applicable law. If you are using the Service on behalf of a business entity, that entity must be legally formed and in good standing.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.3 Service Availability</h3>
            <p class="mb-4">
              The Service is currently offered to customers located in the United States. We do not currently market to, or provide localized compliance support for, customers in the European Union, European Economic Area, United Kingdom, or Switzerland. If you access the Service from outside the United States, you do so at your own discretion and are responsible for compliance with your local laws.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.4 Account Security</h3>
            <p class="mb-3">You are responsible for:</p>
            <ul class="ml-5 list-disc space-y-2 mb-3">
              <li>Maintaining the confidentiality of your API keys and endpoint secrets.</li>
              <li>All activity that occurs under your account, whether or not authorized by you.</li>
              <li>Promptly notifying us at <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a> if you suspect unauthorized access to your account.</li>
            </ul>
            <p class="mb-4">
              We will never ask you for your API keys or endpoint secrets. Endpoint secrets are stored only as one-way cryptographic hashes and cannot be recovered — only rotated.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">3.5 API Key Management</h3>
            <p>
              You may maintain up to two active API keys per account to enable zero-downtime rotation. You are responsible for rotating keys promptly if you believe they have been compromised. Revoked keys are permanently deleted after ninety (90) days.
            </p>
          </section>

          {/* ── Section 4 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">4. Pricing and Payment</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.1 Pricing Tiers</h3>
            <p class="mb-3">The Service is offered under the following tiers:</p>

            <p class="mb-2"><strong>Free Tier:</strong></p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>No credit card required.</li>
              <li>Up to five (5) webhook ingest events per month.</li>
              <li>1-day payload retention.</li>
              <li>If the monthly event limit is reached, ingest is paused until the next billing period or until you upgrade.</li>
            </ul>

            <p class="mb-2"><strong>Pay-As-You-Go Tier:</strong></p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>One-time activation fee of $25.00 USD, which includes $25.00 USD in usage credits applied to your first invoice.</li>
              <li>Metered pricing:
                <ul class="ml-5 list-disc space-y-2 mt-2">
                  <li><strong>$0.03 per ingest event</strong> (each webhook payload accepted by the Service).</li>
                  <li><strong>$0.10 per GB per month</strong> of stored data, calculated daily based on actual storage consumption.</li>
                </ul>
              </li>
              <li>No minimum commitment. You pay only for what you use beyond the included credits.</li>
            </ul>

            <p class="mb-2"><strong>Business Tier:</strong></p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Custom pricing available by contacting us at <a href="mailto:sales@webhookstorage.com" class="text-teal-600 hover:underline">sales@webhookstorage.com</a>.</li>
              <li>May include volume discounts, dedicated support, custom SLAs, and data processing agreements.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.2 Metered Billing</h3>
            <p class="mb-3">
              Usage is measured by the Service's metering infrastructure and reported to our payment processor (Stripe). Our metering records are the authoritative source for usage calculations. We measure:
            </p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li><strong>Ingest events:</strong> Each HTTP request that results in a successfully stored payload counts as one ingest event.</li>
              <li><strong>Storage:</strong> Measured daily at 01:00 UTC as a snapshot of your total stored data. Your monthly storage charge is the sum of daily measurements, expressed as GB-months.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.3 Payment Processing</h3>
            <p class="mb-4">
              All payments are processed by Stripe. By upgrading to a paid tier, you authorize Stripe to charge your payment method for the activation fee and all subsequent metered usage. You agree to Stripe's terms of service at <a href="https://stripe.com/legal" class="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">https://stripe.com/legal</a>.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.4 Invoicing and Failed Payments</h3>
            <p class="mb-3">Invoices are generated monthly by Stripe. If a payment fails:</p>
            <ol class="ml-5 list-decimal space-y-2 mb-4">
              <li>Stripe will attempt to retry the charge using smart retry logic.</li>
              <li>If payment remains unsuccessful, we will notify you by email.</li>
              <li>If payment is not resolved within thirty (30) days, your account will be downgraded to the Free Tier and ingest may be paused.</li>
              <li>Persistent non-payment may result in account suspension or termination under Section 11.</li>
            </ol>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.5 Refund Policy</h3>
            <p class="mb-3">
              Because the Service is usage-based, charges reflect actual consumption and are generally non-refundable. If you believe a billing error has occurred, contact us at <a href="mailto:billing@webhookstorage.com" class="text-teal-600 hover:underline">billing@webhookstorage.com</a> within thirty (30) days of the invoice date. We will investigate and issue a correction or credit if an error is confirmed.
            </p>
            <p class="mb-4">
              The one-time activation fee is non-refundable once any portion of the associated usage credits has been consumed. If you cancel within seven (7) days of activation and have not consumed any usage credits, the activation fee will be automatically refunded to your original payment method. No action is required on your part beyond initiating cancellation.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.6 Price Changes</h3>
            <p class="mb-4">
              We may change pricing with at least sixty (60) days' prior written notice. Price changes will not affect usage incurred before the effective date. If you do not agree to the new pricing, you may terminate your account before the new prices take effect.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">4.7 Taxes</h3>
            <p>
              All prices are exclusive of applicable taxes. You are responsible for any sales, use, VAT, GST, or other taxes imposed by your jurisdiction, excluding taxes based on our net income.
            </p>
          </section>

          {/* ── Section 5 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">5. Acceptable Use Policy</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">5.1 Permitted Use</h3>
            <p class="mb-4">
              You may use the Service to receive, store, and retrieve webhook payloads in connection with your legitimate business or personal automation workflows.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">5.2 Prohibited Use</h3>
            <p class="mb-3">You agree not to use the Service to:</p>
            <ol class="ml-5 list-decimal space-y-2 mb-4">
              <li><strong>Store illegal content:</strong> Payloads containing content that is illegal under United States federal law or the laws of the State of Missouri.</li>
              <li><strong>Distribute malware:</strong> Payloads intentionally containing viruses, ransomware, trojan horses, or other malicious code intended to harm recipients.</li>
              <li><strong>Facilitate fraud:</strong> Using the Service as part of any scheme to defraud, deceive, or mislead others.</li>
              <li><strong>Abuse the infrastructure:</strong> Attempting to circumvent rate limits, authentication controls, tenant isolation, or other security measures.</li>
              <li><strong>Conduct attacks:</strong> Using the Service to conduct, facilitate, or participate in denial-of-service attacks, credential stuffing, or other offensive operations against any system.</li>
              <li><strong>Resell without authorization:</strong> Sublicensing or reselling access to the Service without our prior written consent.</li>
              <li><strong>Violate third-party rights:</strong> Storing payloads that you know infringe on the intellectual property, privacy, or other legal rights of third parties.</li>
              <li><strong>Circumvent billing:</strong> Manipulating usage metrics, creating multiple free-tier accounts to avoid paid usage, or otherwise evading charges.</li>
            </ol>

            <h3 class="mb-2 text-base font-semibold text-slate-900">5.3 Content Responsibility</h3>
            <p class="mb-4">
              You are solely responsible for the content of all webhook payloads received and stored through your endpoints. The Service does not monitor, filter, inspect, or moderate payload content. You represent and warrant that you have all necessary rights and authorizations to receive and store the data transmitted to your endpoints.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">5.4 Rate Limits and Fair Use</h3>
            <p class="mb-4">
              We may impose reasonable rate limits on ingest endpoints and API requests to protect Service stability. Current limits are published in our documentation. We will provide notice before reducing published limits.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">5.5 Enforcement</h3>
            <p>
              We reserve the right to suspend or terminate accounts that violate this Acceptable Use Policy. Where practicable, we will provide notice and an opportunity to cure before taking action. We may act immediately and without notice if the violation poses a threat to the Service, our infrastructure, other customers, or third parties.
            </p>
          </section>

          {/* ── Section 6 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">6. Intellectual Property</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">6.1 Your Data</h3>
            <p class="mb-4">
              You retain all right, title, and interest in your data, including all webhook payloads stored through the Service ("Customer Data"). We do not acquire any ownership interest in Customer Data.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">6.2 License to Operate</h3>
            <p class="mb-4">
              You grant us a limited, non-exclusive, worldwide license to store, process, and transmit Customer Data solely as necessary to provide the Service to you. This license terminates when Customer Data is deleted from the Service, whether by you, by automated retention policies, or upon account termination.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">6.3 Our Intellectual Property</h3>
            <p class="mb-4">
              The Service, including its software, APIs, documentation, trademarks, and design, is owned by Sammons Software LLC and protected by intellectual property laws. These Terms do not grant you any license to our intellectual property except the limited right to access and use the Service in accordance with these Terms.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">6.4 Feedback</h3>
            <p>
              If you provide us with suggestions, enhancement requests, or other feedback about the Service ("Feedback"), you grant us a perpetual, irrevocable, royalty-free license to use, modify, and incorporate that Feedback into the Service without restriction or obligation to you.
            </p>
          </section>

          {/* ── Section 7 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">7. DMCA and Copyright</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">7.1 Safe Harbor</h3>
            <p class="mb-4">
              The Service stores webhook payloads on behalf of customers without knowledge of or control over their content. We operate as a service provider within the meaning of 17 U.S.C. Section 512(c) and claim safe harbor protections thereunder.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">7.2 Designated Agent</h3>
            <p class="mb-3">
              If you believe that material stored through the Service infringes your copyright, you may submit a notice of claimed infringement to our designated DMCA agent:
            </p>
            <p class="mb-3">
              <strong>DMCA Agent</strong><br />
              Sammons Software LLC<br />
              O'Fallon, Missouri<br />
              Email: <a href="mailto:dmca@webhookstorage.com" class="text-teal-600 hover:underline">dmca@webhookstorage.com</a>
            </p>
            <p class="mb-3">Your notice must include:</p>
            <ol class="ml-5 list-decimal space-y-2 mb-4">
              <li>Identification of the copyrighted work claimed to be infringed.</li>
              <li>Identification of the material claimed to be infringing and information reasonably sufficient to locate it within the Service.</li>
              <li>Your contact information (name, address, telephone, email).</li>
              <li>A statement that you have a good faith belief that the use is not authorized by the copyright owner, its agent, or the law.</li>
              <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or authorized to act on their behalf.</li>
              <li>Your physical or electronic signature.</li>
            </ol>

            <h3 class="mb-2 text-base font-semibold text-slate-900">7.3 Response to Notices</h3>
            <p>
              Upon receipt of a valid DMCA notice, we will act expeditiously to remove or disable access to the identified material. We will notify the affected Customer, who may submit a counter-notification as permitted under 17 U.S.C. Section 512(g).
            </p>
          </section>

          {/* ── Section 8 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">8. Data Handling, Retention, and Security</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.1 Data Storage and Encryption</h3>
            <p class="mb-4">
              Customer Data is stored in Amazon Web Services (AWS) infrastructure in the United States (currently us-east-1). All Customer Data is encrypted at rest using AWS Key Management Service (KMS) and encrypted in transit using TLS.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.2 Tenant Isolation</h3>
            <p class="mb-4">
              Customer Data is logically isolated by tenant. Each customer's data is stored with unique prefixes and partition keys that prevent cross-tenant access at the application and infrastructure layers. We employ multiple layers of isolation including authentication verification, application-layer tenant checks, and infrastructure-level access controls.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.3 Retention</h3>
            <p class="mb-3">
              You may configure data retention periods per endpoint. Available retention periods range from one (1) day to three hundred sixty-five (365) days. The default retention period is one (1) day.
            </p>
            <p class="mb-3">When a payload's retention period expires:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>The payload is automatically deleted from storage.</li>
              <li>Associated metadata is removed within seven (7) days of payload deletion.</li>
              <li>Billing records related to the payload are retained for ninety (90) days for audit purposes.</li>
            </ul>
            <p class="mb-4">
              You may manually delete individual payloads at any time through the API or dashboard.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.4 Data Deletion on Account Termination</h3>
            <p class="mb-3">Upon account termination (whether initiated by you or by us):</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>A thirty (30) day grace period begins, during which your data remains accessible if you reactivate your account.</li>
              <li>After the grace period, all Customer Data — including payloads, metadata, endpoint configurations, and API keys — is permanently and irreversibly deleted.</li>
              <li>Billing records are retained for the minimum period required by applicable law or our internal audit requirements (currently ninety (90) days post-termination).</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.5 Security Measures</h3>
            <p class="mb-3">We implement reasonable technical and organizational measures to protect Customer Data, including:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Encryption at rest (AES-256 via AWS KMS) and in transit (TLS 1.2+).</li>
              <li>One-way cryptographic hashing of all credentials (endpoint secrets and API keys).</li>
              <li>Timing-safe credential verification to resist side-channel attacks.</li>
              <li>Least-privilege IAM roles for all service components.</li>
              <li>Input validation at all API boundaries.</li>
              <li>Time-limited (15-minute) presigned URLs for payload downloads.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">8.6 Security Incidents</h3>
            <p class="mb-3">If we become aware of a confirmed security breach affecting Customer Data, we will:</p>
            <ol class="ml-5 list-decimal space-y-2">
              <li>Notify affected customers by email without unreasonable delay, and in no event later than seventy-two (72) hours after confirmation of the breach.</li>
              <li>Provide a description of the nature of the breach and the categories of data affected.</li>
              <li>Describe the measures taken or proposed to address the breach and mitigate its effects.</li>
              <li>Cooperate with your reasonable investigation requests.</li>
            </ol>
          </section>

          {/* ── Section 9 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">9. Privacy and Data Protection</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">9.1 Privacy Policy</h3>
            <p class="mb-4">
              Our collection and use of personal information is governed by our Privacy Policy, available at <a href="/privacy" class="text-teal-600 hover:underline">webhookstorage.com/privacy</a>. The Privacy Policy is incorporated into these Terms by reference.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">9.2 Data Processing</h3>
            <p class="mb-3">With respect to Customer Data containing personal information:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li><strong>You</strong> are the data controller (or equivalent under applicable law). You determine what data is transmitted to your endpoints and stored through the Service.</li>
              <li><strong>We</strong> are a data processor (or equivalent). We process Customer Data only as necessary to provide the Service and as instructed by you through your use of the Service.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">9.3 Data Processing Agreements</h3>
            <p class="mb-4">
              A Data Processing Agreement ("DPA") is available for review and acceptance in your account dashboard at <strong>webhookstorage.com/dashboard/legal</strong>. Acceptance of the DPA is optional and supplements these Terms. The DPA governs our processing of personal data contained in Customer Data in compliance with GDPR, CCPA, and other applicable data protection regulations. Business Tier customers may negotiate custom data processing terms as part of their agreement.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">9.4 Subprocessors</h3>
            <p class="mb-3">We use the following categories of subprocessors to provide the Service:</p>
            <ul class="ml-5 list-disc space-y-2 mb-3">
              <li><strong>Cloud infrastructure:</strong> Amazon Web Services (data storage, compute, networking)</li>
              <li><strong>Payment processing:</strong> Stripe (billing, payment method storage)</li>
              <li><strong>Authentication:</strong> WorkOS (identity verification, session management)</li>
              <li><strong>Email:</strong> Amazon SES (transactional emails)</li>
            </ul>
            <p class="mb-4">A current list of specific subprocessors is available upon request.</p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">9.5 Data Residency</h3>
            <p>
              Customer Data is currently stored in AWS us-east-1 (N. Virginia, USA). We do not currently offer data residency options in other regions. If data residency is a requirement for your use case, contact us to discuss Business Tier options.
            </p>
          </section>

          {/* ── Section 10 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">10. Disclaimer of Warranties</h2>
            <p class="mb-3">
              <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</strong>
            </p>
            <p class="mb-3">Without limiting the foregoing, we do not warrant that:</p>
            <ul class="ml-5 list-disc space-y-2 mb-3">
              <li>The Service will be uninterrupted, error-free, or secure.</li>
              <li>Defects will be corrected within any specific timeframe.</li>
              <li>The Service will meet your specific requirements.</li>
              <li>The results obtained from the Service will be accurate or reliable.</li>
            </ul>
            <p>
              We do not offer a service-level agreement (SLA) for uptime or availability on the Free or Pay-As-You-Go tiers. Business Tier customers may negotiate SLAs as part of their agreement. See Section 10A below for our general service-level commitments.
            </p>
          </section>

          {/* ── Section 10A ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">10A. Service Level Agreement</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">10A.1 Service Availability Target</h3>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li><strong>Self-serve tiers (Free, Activated):</strong> 99.5% monthly uptime.</li>
              <li><strong>Business tier:</strong> 99.9% monthly uptime (or as negotiated in your Business agreement).</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">10A.2 Measurement</h3>
            <p class="mb-4">
              Availability is measured as the percentage of minutes in a calendar month during which the ingest endpoint (<code>in.webhookstorage.com</code>) returns HTTP 200 to automated health checks.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">10A.3 Exclusions</h3>
            <p class="mb-3">The following are excluded from uptime calculations:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li>Scheduled maintenance with at least 48 hours advance notice.</li>
              <li>Force majeure events as defined in Section 16.5.</li>
              <li>Issues caused by factors outside our reasonable control, including upstream provider outages, DNS failures, and internet backbone disruptions.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">10A.4 Service Credits for SLA Breach</h3>
            <div class="overflow-x-auto mb-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="pb-2 pr-4 font-semibold text-slate-900">Monthly Uptime</th>
                    <th class="pb-2 font-semibold text-slate-900">Service Credit</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr>
                    <td class="py-2 pr-4">&lt; 99.5% (self-serve) or &lt; 99.9% (Business)</td>
                    <td class="py-2">10% credit on that month's charges</td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4">&lt; 99.0%</td>
                    <td class="py-2">25% credit</td>
                  </tr>
                  <tr>
                    <td class="py-2 pr-4">&lt; 95.0%</td>
                    <td class="py-2">50% credit</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mb-3">
              Credits are capped at 100% of one month's charges. To request a credit, contact{" "}
              <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a>{" "}
              within 30 days of the affected month with evidence of the outage.
            </p>
            <p>
              Service credits are the sole and exclusive remedy for any SLA breach under this section.
            </p>
          </section>

          {/* ── Section 11 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">11. Limitation of Liability</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">11.1 Exclusion of Consequential Damages</h3>
            <p class="mb-4">
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SAMMONS SOFTWARE LLC, ITS OFFICERS, MEMBERS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, REVENUE, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR USE OF THE SERVICE, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, STRICT LIABILITY, OR OTHERWISE) AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</strong>
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">11.2 Cap on Liability</h3>
            <p class="mb-4">
              <strong>OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE TOTAL AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.</strong>
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">11.3 Exceptions</h3>
            <p class="mb-4">
              The limitations in this Section 11 do not apply to: (a) our obligations under Section 8.6 (Security Incidents); (b) either party's indemnification obligations; or (c) liability that cannot be excluded or limited under applicable law.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">11.4 Basis of the Bargain</h3>
            <p>
              You acknowledge that the pricing of the Service reflects the allocation of risk set forth in this Section 11 and that we would not provide the Service without these limitations.
            </p>
          </section>

          {/* ── Section 12 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">12. Indemnification</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">12.1 Your Indemnification of Us</h3>
            <p class="mb-3">
              You agree to indemnify, defend, and hold harmless Sammons Software LLC and its officers, members, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ol class="ml-5 list-decimal space-y-2 mb-4">
              <li>Your use of the Service in violation of these Terms.</li>
              <li>The content of Customer Data stored through the Service.</li>
              <li>Your violation of any applicable law, regulation, or third-party right.</li>
              <li>Any dispute between you and any webhook sender or consumer using your endpoints or API keys.</li>
            </ol>

            <h3 class="mb-2 text-base font-semibold text-slate-900">12.2 Indemnification Procedure</h3>
            <p>
              We will promptly notify you of any claim subject to indemnification (though failure to do so will not relieve your obligations except to the extent you are materially prejudiced). You will have sole control over the defense and settlement of any such claim, provided that you may not settle any claim in a manner that imposes obligations on us or admits fault on our behalf without our prior written consent.
            </p>
          </section>

          {/* ── Section 13 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">13. Term and Termination</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">13.1 Term</h3>
            <p class="mb-4">
              These Terms are effective when you create an account and remain in effect until terminated by either party.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">13.2 Termination by You</h3>
            <p class="mb-4">
              You may terminate your account at any time through the Service dashboard or by contacting <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a>. Termination does not relieve you of any obligation to pay outstanding charges incurred before the termination date.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">13.3 Termination by Us</h3>
            <p class="mb-3">We may terminate or suspend your account:</p>
            <ul class="ml-5 list-disc space-y-2 mb-4">
              <li><strong>For cause:</strong> Immediately, if you materially breach these Terms (including the Acceptable Use Policy), engage in fraud, or fail to pay outstanding charges after notice and a reasonable cure period.</li>
              <li><strong>For convenience:</strong> With thirty (30) days' prior written notice for any reason.</li>
            </ul>

            <h3 class="mb-2 text-base font-semibold text-slate-900">13.4 Effect of Termination</h3>
            <p class="mb-3">Upon termination:</p>
            <ol class="ml-5 list-decimal space-y-2">
              <li>Your right to access the Service ceases immediately (or at the end of any grace period we provide).</li>
              <li>The data deletion process described in Section 8.4 begins.</li>
              <li>Sections that by their nature should survive termination will survive, including Sections 4.5 (Refund Policy), 6 (Intellectual Property), 10 (Disclaimer of Warranties), 11 (Limitation of Liability), 12 (Indemnification), 14 (Governing Law), 15 (Dispute Resolution), and 16 (General Provisions).</li>
            </ol>
          </section>

          {/* ── Section 14 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">14. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of Missouri, United States, without regard to its conflict-of-laws principles.
            </p>
          </section>

          {/* ── Section 15 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">15. Dispute Resolution</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.1 Informal Resolution</h3>
            <p class="mb-4">
              Before initiating any formal proceeding, you agree to contact us at <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a> and attempt to resolve any dispute informally for at least thirty (30) days. Most disputes can be resolved quickly through good-faith communication.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.2 Binding Arbitration</h3>
            <p class="mb-3">
              <strong>Any dispute, claim, or controversy arising out of or relating to these Terms or the Service ("Dispute") that is not resolved through informal resolution shall be resolved by binding arbitration administered by the American Arbitration Association ("AAA") under the rules specified below, as modified by this Section 15.</strong>
            </p>
            <p class="mb-3">Arbitration terms:</p>
            <ol class="ml-5 list-decimal space-y-2 mb-4">
              <li><strong>Arbitrator:</strong> A single arbitrator selected in accordance with AAA rules.</li>
              <li><strong>Location:</strong> Arbitration shall be conducted in St. Charles County, Missouri, unless both parties agree to a different location or to proceedings conducted by videoconference.</li>
              <li><strong>Governing rules:</strong> If you are using the Service as an individual (not on behalf of a business entity), the AAA Consumer Arbitration Rules apply. If you are using the Service on behalf of a business entity, the AAA Commercial Arbitration Rules apply. In either case, the rules in effect at the time the arbitration is initiated govern, subject to the modifications in this Section 15.</li>
              <li><strong>Authority:</strong> The arbitrator shall have exclusive authority to resolve any dispute relating to the interpretation, applicability, enforceability, or formation of this arbitration agreement, including any claim that all or any part of this agreement is void or voidable.</li>
              <li><strong>Award:</strong> The arbitrator's award shall be final and binding on both parties and may be entered as a judgment in any court of competent jurisdiction.</li>
              <li><strong>Fees:</strong> Each party shall bear its own attorneys' fees and costs, unless the arbitrator determines that a claim was frivolous, in which case the arbitrator may award reasonable attorneys' fees to the prevailing party. If you demonstrate financial hardship, we will consider advancing your share of AAA filing and arbitrator fees.</li>
              <li><strong>Confidentiality:</strong> All aspects of the arbitration proceeding, including the award, shall be treated as confidential by both parties, except as necessary to enforce the award or as required by law.</li>
            </ol>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.3 Opt-Out Right</h3>
            <p class="mb-4">
              <strong>You may opt out of this arbitration agreement within thirty (30) days of creating your account</strong> by sending written notice to <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a> with the subject line "Arbitration Opt-Out" that includes your name, account email, and a clear statement that you wish to opt out. If you opt out, Disputes will be resolved exclusively in the state courts of St. Charles County, Missouri (11th Judicial Circuit) or the United States District Court for the Eastern District of Missouri, and each party irrevocably consents to the personal jurisdiction and venue of such courts.
            </p>
            <p class="mb-4">
              Opting out of arbitration will not affect any other provision of these Terms and will not be grounds for account termination or any adverse action.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.4 Class Action and Jury Trial Waiver</h3>
            <p class="mb-3"><strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong></p>
            <p class="mb-3">
              <strong>(A) YOU AND SAMMONS SOFTWARE LLC AGREE THAT ANY DISPUTE WILL BE RESOLVED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE PERSON'S CLAIMS AND MAY NOT PRESIDE OVER ANY FORM OF CLASS OR REPRESENTATIVE PROCEEDING.</strong>
            </p>
            <p class="mb-3">
              <strong>(B) YOU AND SAMMONS SOFTWARE LLC WAIVE THE RIGHT TO A TRIAL BY JURY FOR ANY DISPUTE THAT IS NOT SUBJECT TO ARBITRATION UNDER THIS SECTION 15 (INCLUDING IF YOU HAVE OPTED OUT OF ARBITRATION).</strong>
            </p>
            <p class="mb-4">
              If a court of competent jurisdiction finds that the class action waiver in this Section 15.4(A) is unenforceable as to a particular claim or request for relief, then that claim or request for relief — and only that claim or request for relief — shall be severed from arbitration and may be brought in court, subject to Section 15.3's venue provisions.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.5 Jurisdiction and Venue</h3>
            <p class="mb-4">
              For any Dispute not subject to arbitration (including if you have exercised the opt-out right under Section 15.3, or for claims seeking injunctive relief to protect intellectual property), the exclusive jurisdiction and venue shall be the state courts of St. Charles County, Missouri (11th Judicial Circuit) or the United States District Court for the Eastern District of Missouri. Each party irrevocably consents to the personal jurisdiction of such courts.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">15.6 Small Claims Exception</h3>
            <p>
              Notwithstanding the foregoing, either party may bring an individual action in small claims court in St. Charles County, Missouri, if the claim qualifies for small claims jurisdiction.
            </p>
          </section>

          {/* ── Section 16 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">16. General Provisions</h2>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.1 Entire Agreement</h3>
            <p class="mb-4">
              These Terms, together with the Privacy Policy and any applicable DPA or Business Tier agreement, constitute the entire agreement between you and Sammons Software LLC regarding the Service and supersede all prior agreements and understandings.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.2 Severability</h3>
            <p class="mb-4">
              If any provision of these Terms is held to be unenforceable, the remaining provisions will continue in full force and effect, and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.3 Waiver</h3>
            <p class="mb-4">
              Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.4 Assignment</h3>
            <p class="mb-4">
              You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms in connection with a merger, acquisition, or sale of substantially all of our assets upon notice to you.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.5 Force Majeure</h3>
            <p class="mb-4">
              Neither party will be liable for failure or delay in performing its obligations (except payment obligations) due to circumstances beyond its reasonable control, including natural disasters, war, terrorism, labor disputes, government actions, pandemics, or failures of third-party infrastructure providers (including Amazon Web Services outages), internet backbone disruptions, or widespread network failures.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.6 Notices</h3>
            <p class="mb-3">
              Notices to you will be sent to the email address associated with your account. Notices to us should be sent to <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a> or by mail to:
            </p>
            <p class="mb-4">
              Sammons Software LLC<br />
              O'Fallon, Missouri
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.7 No Third-Party Beneficiaries</h3>
            <p class="mb-4">
              These Terms do not create any third-party beneficiary rights in any person or entity.
            </p>

            <h3 class="mb-2 text-base font-semibold text-slate-900">16.8 Export Compliance</h3>
            <p>
              You agree to comply with all applicable export control and sanctions laws and regulations. You represent that you are not located in, or a national or resident of, any country subject to U.S. trade sanctions, and that you are not on any U.S. government restricted-parties list.
            </p>
          </section>

          {/* ── Section 17 ── */}
          <section>
            <h2 class="mb-3 text-lg font-semibold text-slate-900">17. Contact Information</h2>
            <p class="mb-3">For questions about these Terms:</p>
            <p class="mb-4">
              <strong>Sammons Software LLC</strong><br />
              O'Fallon, Missouri<br />
              Email: <a href="mailto:legal@webhookstorage.com" class="text-teal-600 hover:underline">legal@webhookstorage.com</a>
            </p>
            <p>
              For billing inquiries: <a href="mailto:billing@webhookstorage.com" class="text-teal-600 hover:underline">billing@webhookstorage.com</a><br />
              For support: <a href="mailto:support@webhookstorage.com" class="text-teal-600 hover:underline">support@webhookstorage.com</a><br />
              For DMCA notices: <a href="mailto:dmca@webhookstorage.com" class="text-teal-600 hover:underline">dmca@webhookstorage.com</a>
            </p>
          </section>

        </div>

      </div>
      <PublicFooter />
    </div>
  );
}
